import { FormTipTapBlockEditor } from "@circle-react-uikit/Form/FormTipTapBlockEditor/FormTipTapBlockEditor";
import { ToolbarCompass } from "@circle-react-uikit/TipTapBlockEditor/Toolbar/ToolbarCompass";
import type { EditorApi } from "@circle-react-uikit/TipTapBlockEditor/types";
import { IconButton } from "@circleco/compass/components/IconButton";
import type { ComponentPropsWithoutRef, ComponentType, Ref } from "react";
import { useId, useRef } from "react";
import { useFormState } from "react-hook-form";

import { t } from "@/i18n-js/instance";
import { DISABLED_EDITOR_EXTENSIONS } from "@/react/components/CommentsCompass/constants";
import { isTiptapDocumentBlank } from "@/react/components/CommentsCompass/helpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useCanRecordVoiceInPosts } from "@/react/hooks/useCanRecordVoiceInPosts";
import { useCanUploadMedia } from "@/react/hooks/useCanUploadMedia";
import { useSubmitViaKeyboard } from "@/react/hooks/utils/useSubmitViaKeyboard";

import { useTiptapBody } from "./useTiptapBody";

export interface EditorProps {
  autoFocus?: boolean;
  editorApiRef?: Ref<EditorApi>;
  formId?: string;
  hideSubmitButton?: boolean;
  isCollapsed?: boolean;
  placeholder?: string;
  shouldSupportTables?: boolean;
  spaceId: number;
  submitLabel?: string;
  toolbar?: ComponentType<ComponentPropsWithoutRef<typeof ToolbarCompass>>;
}

export const Editor = ({
  autoFocus = false,
  editorApiRef,
  formId,
  hideSubmitButton = false,
  isCollapsed = false,
  placeholder = t("comments.form.fields.body_placeholder"),
  shouldSupportTables = false,
  spaceId,
  submitLabel = t("comments.create.actions.submit"),
  toolbar: ToolbarComponent = ToolbarCompass,
}: EditorProps) => {
  const isMediaUploadAllowed = useCanUploadMedia();
  const isVoiceMessageAllowed = useCanRecordVoiceInPosts();
  const { isSubmitting } = useFormState();

  const {
    body,
    sgidMap,
    inlineAttachments,
    mergeSgidMap,
    mergeInlineAttachments,
  } = useTiptapBody();

  const isEditorEmpty = isTiptapDocumentBlank(body);
  const isSubmitDisabled = isEditorEmpty || isSubmitting;

  const containerRef = useRef<HTMLDivElement>(null);
  useSubmitViaKeyboard({
    enabled: !isSubmitDisabled,
    targetRef: containerRef,
  });

  const editorId = useId().replaceAll(":", "");
  const toolbarPortalId = `comment-toolbar-${editorId}`;

  return (
    <div
      ref={containerRef}
      className={classNames(
        "rounded-xl border border-primary transition-all duration-300 ease-in-out focus-within:border-light active:border-light",
        isCollapsed ? "py-[9px]" : "pb-2 pt-3",
        { "pointer-events-none opacity-50": isSubmitting },
      )}
    >
      <div className="flex min-h-6 items-center px-4">
        <FormTipTapBlockEditor
          variant="compact"
          editorApiRef={editorApiRef}
          autofocus={autoFocus ? "end" : false}
          placeholder={placeholder}
          name="tiptap_body.body"
          toolbars={[
            { portalId: toolbarPortalId, component: ToolbarComponent },
          ]}
          className="max-h-72 w-full overflow-y-auto"
          spaceId={spaceId}
          sgidToObjectMap={sgidMap}
          inlineAttachments={inlineAttachments}
          onLocalSgidToObjectMapChange={mergeSgidMap}
          onLocalAttachmentsChange={mergeInlineAttachments}
          disabledExtensions={DISABLED_EDITOR_EXTENSIONS}
          isVoiceMessageAllowed={isVoiceMessageAllowed}
          isMediaUploadAllowed={isMediaUploadAllowed}
          isVideoClipAllowed
          allowBubbleMenuOverflow={false}
          shouldSupportTables={shouldSupportTables}
        />
      </div>
      <div
        className={classNames(
          "flex items-end justify-between overflow-hidden px-2 transition-all duration-300 ease-in-out",
          isCollapsed ? "h-0 opacity-0" : "h-[50px] opacity-100",
        )}
      >
        <div
          id={toolbarPortalId}
          className="scrollbar-hide min-w-0 overflow-x-auto overflow-y-hidden"
        />
        {!hideSubmitButton && (
          <IconButton
            type="submit"
            form={formId}
            variant="branded"
            size="md"
            icon="arrow-up"
            loading={isSubmitting}
            disabled={isSubmitDisabled}
            aria-label={submitLabel}
          />
        )}
      </div>
    </div>
  );
};
